import { SwiperSlide } from "swiper/swiper-react";
import { FreeMode, Navigation, Pagination } from "swiper/modules";
import React, { useEffect, useRef, useState } from "react";
import {
    ArrowIconButton,
    GroupsSwiper,
    SwiperContainer,
} from "./PreviewCardSwiper.styles";
import { PreviewSlider } from "./PreviewSlider";
import { ChevronLeftIcon } from "@icons/ChevronLeftIcon";
import { ChevronRightIcon } from "@icons/ChevronRightIcon";
import { isDeviceOutputDesktop } from "../../../Domain/DeviceOutputState";
import { SkeletonsListCards } from "./SkeletonMediaPreview";
import { PostDetailsActionType } from "../Modals/PostDetailsModal/PostDetailsModalStore";
import { MediaListType } from "@infrastructure/Services/MediaService";
import { useImagesModalStore } from "../Modals/ImagesModal/ImagesModalStore";

const useSwiperRef = <T extends HTMLElement>(): [T | null, React.Ref<T>] => {
    const [wrapper, setWrapper] = useState<T | null>(null);
    const ref = useRef<T>(null);

    useEffect(() => {
        if (ref.current) {
            setWrapper(ref.current);
        }
    }, []);

    return [wrapper, ref];
};

export function MediaListSwiper({
    isLoading,
    onPostCardClick,
    previews,
}: {
    isLoading: boolean;
    onPostCardClick: (postId: string, action?: PostDetailsActionType) => void;
    previews: MediaListType[];
}) {
    if (isLoading) {
        return <SkeletonsListCards />;
    }
    return <ListCards previews={previews} onPostCardClick={onPostCardClick} />;
}

function ListCards({
    previews,
    onPostCardClick,
}: {
    onPostCardClick: (postId: string, action?: PostDetailsActionType) => void;
    previews: MediaListType[];
}) {
    const [nextEl, nextElRef] = useSwiperRef<HTMLButtonElement>();
    const [prevEl, prevElRef] = useSwiperRef<HTMLButtonElement>();

    const isDesktop = isDeviceOutputDesktop();
    let navigation = {};
    if (isDesktop) {
        navigation = {
            prevEl,
            nextEl,
            disabledClass: "hidden",
        };
    }

    const { setIsImagesModalOpen } = useImagesModalStore();

    return (
        <SwiperContainer>
            <GroupsSwiper
                modules={[Navigation, FreeMode, Pagination]}
                pagination={{
                    clickable: false,
                    dynamicBullets: true,
                    dynamicMainBullets: 3,
                }}
                loop={true}
                spaceBetween={"8px"}
                watchOverflow={true}
                freeMode={true}
                breakpointsBase={"container"}
                breakpoints={{
                    0: {
                        slidesPerView: Math.min(previews.length, 1.6),
                    },
                    358: {
                        slidesPerView: Math.min(previews.length, 1.6),
                    },
                    608: {
                        slidesPerView: 3,
                    },
                    992: {
                        slidesPerView: 4,
                    },
                }}
                direction={"horizontal"}
                navigation={navigation}
            >
                {previews.map((media, key) => (
                    <SwiperSlide key={key}>
                        <PreviewSlider
                            showMoreImagesOverlay={key === previews.length - 1}
                            media={media}
                            onPostCardClick={
                                key === previews.length - 1
                                    ? () => {
                                          setIsImagesModalOpen(true);
                                      }
                                    : onPostCardClick
                            }
                        />
                    </SwiperSlide>
                ))}

                {isDesktop && (
                    <>
                        <div ref={prevElRef} className="swiper-button-prev">
                            <ArrowIconButton>
                                <ChevronLeftIcon />
                            </ArrowIconButton>
                        </div>
                        <div ref={nextElRef} className="swiper-button-next">
                            <ArrowIconButton>
                                <ChevronRightIcon />
                            </ArrowIconButton>
                        </div>
                    </>
                )}
            </GroupsSwiper>
        </SwiperContainer>
    );
}
